import * as React from 'react';

import useInvalidateNaptimeCache from 'bundles/enterprise-admin-hooks/hooks/useInvalidateNaptimeCache';
import type { MessageFrequency, SentMessageRow } from 'bundles/enterprise-admin-messaging-v2/utils/types';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import { useNaptime } from 'bundles/naptimejs';
import EnterpriseSentMessages from 'bundles/naptimejs/resources/enterpriseSentMessages.v1';

export const DEFAULT_SENT_MESSAGES_PAGE_SIZE = 10;

/** Fields the `enterpriseSentMessages.v1` list endpoint supports today. */
const SENT_MESSAGE_FIELDS = [
  'id',
  'subject',
  'sentAt',
  'senderName',
  'numberOfRecipients',
  'status',
  'preset',
  'filterParams',
  'enrichedFilterParams',
  'callToAction',
  'cmlContent',
  'replyTo',
  'authorEmail',
  'authorName',
  'authorUserId',
  'scopedTriggerId',
  'frequency',
  'targetAudience',
  'openRate',
  'ctr',
];

/** Known send-cadence values (the UI's upper-case form). */
const MESSAGE_FREQUENCIES: readonly MessageFrequency[] = ['ONE_TIME', 'AUTO_SEND', 'PAUSED'];

/**
 * Normalizes the backend cadence to the UI's `MessageFrequency`. The endpoint
 * returns upper-case enums (e.g. `ONE_TIME`), so we validate against the known
 * values and return `undefined` for anything unrecognized (renders as a dash).
 */
const toFrequency = (value: string | undefined): MessageFrequency | undefined => {
  return MESSAGE_FREQUENCIES.find((frequency) => frequency === value);
};

/**
 * `cmlContent` is a typed-definition union (`{ typeName: 'cml', definition }`).
 * `definition.value` is the authored CML source (`<co-content>…`).
 */
type NaptimeCmlContent = {
  definition?: {
    value?: string;
  };
};

type NaptimeSentMessage = {
  id: string;
  subject?: string;
  senderName?: string;
  authorName?: string;
  authorEmail?: string;
  authorUserId?: number;
  numberOfRecipients?: number;
  /** Epoch milliseconds. */
  sentAt?: number;
  cmlContent?: NaptimeCmlContent;
  /** Id of the scoped trigger backing an automated message; absent for one-off sends. */
  scopedTriggerId?: string;
  /** Send cadence (`ONE_TIME` | `AUTO_SEND` | `PAUSED`). */
  frequency?: string;
  /** Plain-language audience label (e.g. "Inactive learners"). */
  targetAudience?: string;
  /** Delivery status; `IN_PROGRESS` while the message is still being sent out. */
  status?: string;
  /** Open rate as `{ rate, learnerCount }`; `rate` is a percentage (0–100). */
  openRate?: NaptimeMetric;
  /** Clicks rate as `{ rate, learnerCount }`; `rate` is a percentage (0–100). */
  ctr?: NaptimeMetric;
  /** Audience filter params from the original send (filterId, orgId, programIds). */
  filterParams?: { filterId?: string; orgId?: string; programIds?: string[] };
  /** Legacy preset id (e.g. `remind-join`, `custom`). */
  preset?: string;
  /** CTA button type (e.g. `JOIN_PROGRAM`, `DO_NOT_INCLUDE`). */
  callToAction?: string;
};

/** Engagement metric shape returned by the list endpoint (open rate / clicks). */
type NaptimeMetric = {
  /** Percentage (0–100). */
  rate?: number;
  /** Number of learners the rate is computed over. */
  learnerCount?: number;
};

const toRow = (message: NaptimeSentMessage): SentMessageRow => ({
  id: message.id,
  subject: message.subject ?? '',
  learnerCount: message.numberOfRecipients ?? 0,
  sentByName: message.senderName ?? '',
  authorName: message.authorName ?? '',
  authorEmail: message.authorEmail ?? '',
  authorUserId: message.authorUserId,
  bodyCml: message.cmlContent?.definition?.value ?? '',
  sentAt: message.sentAt != null ? new Date(message.sentAt).toISOString() : '',
  cohortDescription: message.targetAudience ?? '',
  frequency: toFrequency(message.frequency),
  scopedTriggerId: message.scopedTriggerId,
  isInProgress: message.status === 'IN_PROGRESS',
  openRate: message.openRate?.rate,
  ctr: message.ctr?.rate,
  filterParams: message.filterParams,
  preset: message.preset,
  callToAction: message.callToAction,
});

type Result = {
  /** Current page of rows, mapped into the table's row shape. */
  rows: SentMessageRow[];
  /** Server-reported total across all pages (drives pagination controls). */
  total: number;
  loading: boolean;
  error: boolean;
  /** 1-indexed current page. */
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refresh: () => void;
};

export type SentMessagesFilterParams = {
  /** Admin userId for filtering by sender. Undefined = no filter. */
  authorUserId?: number;
  /** Backend frequency enum (ONE_TIME | AUTO_SEND | PAUSED). Undefined = no filter. */
  frequency?: string;
  /** Epoch ms cutoff — messages sent after this timestamp are included. */
  sentAtAfter: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Computes UTC start-of-day `sentAtAfter` for a given number of days ago. */
const computeSentAtAfter = (daysAgo: number): number => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0) - daysAgo * DAY_MS;
};

/**
 * Loads the org's sent messages from `enterpriseSentMessages.v1`, server-side
 * paginated and filtered. The org id is read from the admin-dashboard
 * `ThirdPartyOrganizationProvider`.
 */
export const useSentMessages = (filterParams?: SentMessagesFilterParams): Result => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const orgId = thirdPartyOrganization?.id;

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSizeRaw] = React.useState(DEFAULT_SENT_MESSAGES_PAGE_SIZE);

  const setPageSize = React.useCallback((nextPageSize: number) => {
    setPageSizeRaw(nextPageSize);
    setPage(1);
  }, []);

  const sentAtAfter = filterParams?.sentAtAfter ?? computeSentAtAfter(365);
  const authorUserId = filterParams?.authorUserId;
  const frequency = filterParams?.frequency;

  const start = (page - 1) * pageSize;

  const {
    data: { sentMessages },
    pending,
    naptime,
  } = useNaptime<{ sentMessages: NaptimeSentMessage[] }>(
    () => ({
      sentMessages: EnterpriseSentMessages.finder('byThirdPartyOrganizationId', {
        params: {
          id: orgId,
          start,
          limit: pageSize,
          sentAtAfter,
          ...(authorUserId != null && { authorUserId }),
          ...(frequency != null && { frequency }),
        },
        fields: SENT_MESSAGE_FIELDS,
        required: false,
      }),
    }),
    [orgId, start, pageSize, sentAtAfter, authorUserId, frequency]
  );

  // @ts-expect-error paging is not present on the inferred naptime type
  const total: number | undefined = naptime?.paging?.sentMessages?.total;
  const rows = React.useMemo(() => (sentMessages ?? []).map(toRow), [sentMessages]);
  const refresh = React.useCallback(
    () => naptime.refreshData({ resources: [EnterpriseSentMessages.RESOURCE_NAME] }),
    [naptime]
  );

  // Expose setPage + the Naptime-backed refresh to the module-level refs so
  // useRefreshSentMessages (called from elsewhere, e.g. the compose modal) can
  // reset pagination AND force a refetch — clearing the cache alone doesn't
  // refetch an already-mounted, unchanged query.
  React.useEffect(() => {
    activeSetPage = setPage;
    activeRefresh = refresh;
    return () => {
      activeSetPage = null;
      activeRefresh = null;
    };
  }, [refresh]);

  return {
    rows,
    total: total ?? rows.length,
    loading: pending,
    error: !pending && !sentMessages,
    page,
    pageSize,
    setPage,
    setPageSize,
    refresh,
  };
};

export default useSentMessages;

/**
 * Module-level refs to the mounted `useSentMessages` table's `setPage` and
 * Naptime-backed `refresh`. Let `useRefreshSentMessages` reset pagination and
 * force a refetch without a context dependency. Null when no table is mounted.
 */
let activeSetPage: ((page: number) => void) | null = null;
let activeRefresh: (() => void) | null = null;

/**
 * Refreshes the sent-messages table after a mutation (e.g. sending a message).
 * Callable from anywhere — the compose modal, the resend/pause row actions —
 * regardless of whether the table itself is currently mounted:
 *
 * - Table mounted: reset to page 1 (new sends land on top) and let
 *   `naptime.refreshData` refetch every page already loaded for this resource
 *   directly from the server. That refetch is non-destructive — it keeps
 *   rendering the current rows while in flight — so the table never drops
 *   into its empty/error state.
 * - No table mounted (e.g. composing from a learner's profile or a
 *   program-card reminder): there's nothing to eagerly refetch, so instead
 *   evict the cached resource. The next time the table mounts it fetches
 *   fresh data instead of serving whatever was cached before this mutation.
 */
export const useRefreshSentMessages = () => {
  const invalidateCache = useInvalidateNaptimeCache();

  return React.useCallback(() => {
    if (activeRefresh) {
      activeSetPage?.(1);
      activeRefresh();
    } else {
      invalidateCache(EnterpriseSentMessages.RESOURCE_NAME);
    }
  }, [invalidateCache]);
};
